import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface BookCopy {
    id: number
    barcode: string
    is_loaned: boolean
}

interface BookDetail {
    book_code: string | null
    title: string | null
    author: string | null
    total_count: number
    available_count: number
    copies: BookCopy[]
}

interface CurrentLoan {
    loan_id: number
    member_name: string | null
    loaned_at: string
}

export default function AdminBookDetail() {
    const { book_code } = useParams<{ book_code: string }>()
    const navigate = useNavigate()

    const [detail, setDetail] = useState<BookDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [isEditing, setIsEditing] = useState(false)
    const [title, setTitle] = useState('')
    const [author, setAuthor] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [copyLoans, setCopyLoans] = useState<Record<number, CurrentLoan | null>>({})

    const [confirmModal, setConfirmModal] = useState<{ open: boolean; copyId: number | null }>({ open: false, copyId: null })
    const [deleteAllModal, setDeleteAllModal] = useState(false)
    const [resultModal, setResultModal] = useState({ open: false, title: '', redirectOnClose: false })

    const fetchDetail = async () => {
        if (!book_code) return
        setLoading(true)
        try {
            const res = await fetchWithAuth(getApiUrl(`/marubit/api/book/${book_code}`))
            if (!res.ok) throw new Error('조회 실패')
            const json = await res.json()
            const data: BookDetail = json.data
            setDetail(data)
            setTitle(data.title ?? '')
            setAuthor(data.author ?? '')

            // 대출 중인 사본의 대출자 정보 조회
            const loanedCopies = data.copies.filter(c => c.is_loaned)
            const loanInfo: Record<number, CurrentLoan | null> = {}
            for (const copy of loanedCopies) {
                try {
                    const bRes = await fetchWithAuth(getApiUrl(`/marubit/api/book/barcode/${copy.barcode}`))
                    if (bRes.ok) {
                        const bJson = await bRes.json()
                        loanInfo[copy.id] = bJson.data.current_loan
                    }
                } catch { /* skip */ }
            }
            setCopyLoans(loanInfo)
        } catch {
            setResultModal({ open: true, title: '도서 정보를 불러오는 데 실패했습니다.', redirectOnClose: true })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchDetail() }, [book_code])

    const handleUpdate = async () => {
        if (!detail || detail.copies.length === 0) return
        if (!title.trim()) { setResultModal({ open: true, title: '도서명을 입력해주세요.', redirectOnClose: false }); return }

        setSubmitting(true)
        try {
            const token = localStorage.getItem('access_token')
            // 동일 book_code의 모든 사본에 같은 제목/저자를 일괄 반영
            for (const copy of detail.copies) {
                const res = await fetch(getApiUrl(`/marubit/api/book/admin/${copy.id}`), {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ title: title.trim(), author: author.trim() || null }),
                })
                if (!res.ok) {
                    const err = await res.json()
                    throw new Error(err?.error?.message || '수정 실패')
                }
            }
            setIsEditing(false)
            setResultModal({ open: true, title: '도서 정보가 수정되었습니다.', redirectOnClose: false })
            fetchDetail()
        } catch (e: any) {
            setResultModal({ open: true, title: e.message ?? '수정에 실패했습니다.', redirectOnClose: false })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeleteCopyClick = (copyId: number) => setConfirmModal({ open: true, copyId })

    const handleDeleteCopyConfirm = async () => {
        if (!confirmModal.copyId) return
        try {
            const token = localStorage.getItem('access_token')
            const res = await fetch(getApiUrl(`/marubit/api/book/admin/${confirmModal.copyId}`), {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            })
            if (!res.ok) {
                const err = await res.json()
                throw new Error(err?.error?.message || '삭제 실패')
            }
            // 마지막 사본이었다면 목록으로 이동
            if (detail && detail.copies.length === 1) {
                navigate('/admin/library')
                return
            }
            setResultModal({ open: true, title: '사본이 삭제되었습니다.', redirectOnClose: false })
            fetchDetail()
        } catch (e: any) {
            setResultModal({ open: true, title: e.message ?? '삭제에 실패했습니다.', redirectOnClose: false })
        } finally {
            setConfirmModal({ open: false, copyId: null })
        }
    }

    const handleDeleteAllConfirm = async () => {
        if (!detail) return
        try {
            const token = localStorage.getItem('access_token')
            for (const copy of detail.copies) {
                const res = await fetch(getApiUrl(`/marubit/api/book/admin/${copy.id}`), {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` },
                })
                if (!res.ok) {
                    const err = await res.json()
                    throw new Error(err?.error?.message || '삭제 실패')
                }
            }
            navigate('/admin/library')
        } catch (e: any) {
            setResultModal({ open: true, title: e.message ?? '삭제에 실패했습니다.', redirectOnClose: false })
        } finally {
            setDeleteAllModal(false)
        }
    }

    const formatDateTime = (dateStr: string) =>
        new Date(dateStr).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center text-gray-300 bg-[#f5f2f0]">불러오는 중...</div>
    }

    if (!detail) return null

    return (
        <div className="bg-[#f5f2f0] min-h-screen p-6">
            <div className="max-w-[800px] mx-auto">

                <div className="mb-6">
                    <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">도서 상세</h1>
                    <p className="text-[13px] text-gray-400">{detail.book_code ?? '-'}</p>
                </div>

                {/* 도서 정보 */}
                <div className="bg-white rounded-2xl border border-[#e8e0dc] p-6 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-3">도서 정보</h2>
                        {!isEditing && (
                            <button onClick={() => setIsEditing(true)} className="px-3 py-1.5 border border-[#887267] text-[#887267] rounded-lg text-[12px] font-medium hover:bg-[#f5f2f0] transition-colors">
                                수정하기
                            </button>
                        )}
                    </div>

                    <div className="flex flex-col gap-4">
                        <div>
                            <label className="block text-[12px] text-gray-400 mb-1">도서코드</label>
                            <p className="px-3 py-2 text-[13px] text-gray-500 bg-[#f5f2f0] rounded-lg font-mono">{detail.book_code ?? '-'}</p>
                        </div>
                        <div>
                            <label className="block text-[12px] text-gray-400 mb-1">도서명 *</label>
                            {isEditing ? (
                                <input value={title} onChange={e => setTitle(e.target.value)}
                                    className="w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]" />
                            ) : (
                                <p className="px-3 py-2 text-[13px] text-[#2a1f1a] bg-[#f5f2f0] rounded-lg">{detail.title}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-[12px] text-gray-400 mb-1">저자</label>
                            {isEditing ? (
                                <input value={author} onChange={e => setAuthor(e.target.value)}
                                    className="w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]" />
                            ) : (
                                <p className="px-3 py-2 text-[13px] text-[#2a1f1a] bg-[#f5f2f0] rounded-lg">{detail.author ?? '없음'}</p>
                            )}
                        </div>
                    </div>

                    {isEditing && (
                        <div className="flex gap-2 mt-5">
                            <button onClick={() => { setIsEditing(false); setTitle(detail.title ?? ''); setAuthor(detail.author ?? '') }}
                                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                            <button onClick={handleUpdate} disabled={submitting}
                                className="flex-1 px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold disabled:opacity-50">
                                {submitting ? '저장 중...' : '저장'}
                            </button>
                        </div>
                    )}
                </div>

                {/* 사본/대출 현황 */}
                <div className="bg-white rounded-2xl border border-[#e8e0dc] p-6 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                    <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-3 mb-5">
                        사본 현황 ({detail.available_count}/{detail.total_count}권 대출 가능)
                    </h2>

                    <table className="w-full">
                        <thead>
                            <tr className="bg-[#ede0d8]">
                                <th className="text-left px-3 py-2 text-[12px] text-[#5a3d32] font-medium rounded-l-lg">바코드</th>
                                <th className="text-center px-3 py-2 text-[12px] text-[#5a3d32] font-medium">대출여부</th>
                                <th className="text-left px-3 py-2 text-[12px] text-[#5a3d32] font-medium">대출자 정보</th>
                                <th className="text-center px-3 py-2 text-[12px] text-[#5a3d32] font-medium rounded-r-lg w-[80px]">관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.copies.map(copy => {
                                const loan = copyLoans[copy.id]
                                return (
                                    <tr key={copy.id} className="border-t border-[#f5f2f0]">
                                        <td className="px-3 py-3 text-[13px] text-[#2a1f1a] font-mono">{copy.barcode}</td>
                                        <td className="px-3 py-3 text-center">
                                            <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${copy.is_loaned ? 'bg-orange-50 text-orange-500' : 'bg-green-50 text-green-600'}`}>
                                                {copy.is_loaned ? '대출중' : '대출가능'}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 text-[12px] text-gray-500">
                                            {copy.is_loaned && loan
                                                ? `${loan.member_name ?? '알 수 없음'} (${formatDateTime(loan.loaned_at)})`
                                                : '-'}
                                        </td>
                                        <td className="px-3 py-3 text-center">
                                            <button
                                                onClick={() => handleDeleteCopyClick(copy.id)}
                                                disabled={copy.is_loaned}
                                                className="px-3 py-1 text-[12px] border border-red-200 text-red-400 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>

                {/* 삭제하기 / 목록으로 */}
                <div className="flex gap-3">
                    <button onClick={() => setDeleteAllModal(true)}
                        disabled={detail.copies.some(c => c.is_loaned)}
                        className="flex-1 h-12 border-[1.5px] border-red-300 text-red-400 rounded-xl text-[15px] font-bold hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        삭제하기
                    </button>
                    <button onClick={() => navigate('/admin/library')}
                        className="flex-1 h-12 border-[1.5px] border-[#887267] text-[#887267] rounded-xl text-[15px] font-bold hover:bg-[#f5f2f0] transition-colors">
                        목록으로
                    </button>
                </div>
            </div>

            {/* 사본 삭제 확인 모달 */}
            {confirmModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
                        <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">이 사본을 삭제하시겠습니까?</h3>
                        <p className="text-[13px] text-gray-400 mb-5">삭제된 데이터는 복구할 수 없습니다.</p>
                        <div className="flex gap-2">
                            <button onClick={() => setConfirmModal({ open: false, copyId: null })}
                                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                            <button onClick={handleDeleteCopyConfirm}
                                className="flex-1 px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 text-[13px] font-semibold">삭제</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 전체 삭제 확인 모달 */}
            {deleteAllModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
                        <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">이 도서를 완전히 삭제하시겠습니까?</h3>
                        <p className="text-[13px] text-gray-400 mb-5">사본 {detail.copies.length}권이 모두 삭제됩니다. 삭제된 데이터는 복구할 수 없습니다.</p>
                        <div className="flex gap-2">
                            <button onClick={() => setDeleteAllModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                            <button onClick={handleDeleteAllConfirm}
                                className="flex-1 px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 text-[13px] font-semibold">삭제</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 결과 모달 */}
            {resultModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
                        <p className="text-[14px] font-semibold text-[#2a1f1a] mb-4">{resultModal.title}</p>
                        <button onClick={() => {
                            setResultModal(prev => ({ ...prev, open: false }))
                            if (resultModal.redirectOnClose) navigate('/admin/library')
                        }}
                            className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">확인</button>
                    </div>
                </div>
            )}
        </div>
    )
}